import {FunctionComponent} from 'react';
import {FormikProps} from 'formik';
import {useTranslation} from 'react-i18next';
import {AdvisorProfileFormValues} from '@/components/advisor-profile-form/interfaces/form-values';
import FieldWrapper from '@/components/form/field-wrapper';
import FormControl from '@/components/form/form-control';
import Input from '@/components/form/input';
import MaskedInput from '@/components/form/masked-input';
import {FieldMetadata} from '@/interfaces/form';

const TEL_INPUT_MASK = '### ###-####';

interface UserContactInfoSectionProps {
  fieldsMetadata: {
    cons4_email: FieldMetadata;
    cons5_email_confirmation: FieldMetadata;
    cons6_phoneNumber: FieldMetadata;
    cons7_phoneExtension: FieldMetadata;
  };
  formik: Pick<FormikProps<Partial<AdvisorProfileFormValues>>, 'handleChange' | 'handleBlur' | 'values' | 'errors'>;
}

export const UserContactInfoSection: FunctionComponent<UserContactInfoSectionProps> = ({
  fieldsMetadata,
  formik: {handleChange, handleBlur, values, errors},
}) => {
  const {t} = useTranslation('common', {keyPrefix: 'components.advisor-profile-form'});

  return (
    <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-2 xl:grid-cols-4">
      <FormControl
        isRequired={fieldsMetadata.cons4_email.isRequired}
        isInError={!!errors.cons4_email}
        errors={[errors.cons4_email ?? '']}
      >
        <FieldWrapper
          label={t('label-advisor-space-email')}
          labelMultiline={false}
          identifier="CONS4"
          hidden={fieldsMetadata.cons4_email.isHidden}
        >
          <Input
            data-testid="CONS4-input"
            id="CONS4"
            maxLength={50}
            name="CONS4"
            onBlur={handleBlur('cons4_email')}
            onChange={handleChange('cons4_email')}
            required
            readOnly={fieldsMetadata.cons4_email.isReadOnly}
            value={values.cons4_email}
          />
        </FieldWrapper>
      </FormControl>

      <FormControl
        isRequired={fieldsMetadata.cons5_email_confirmation.isRequired}
        isInError={!!errors.cons5_email_confirmation}
        errors={[errors.cons5_email_confirmation ?? '']}
      >
        <FieldWrapper
          label={t('label-advisor-space-email-confirmation')}
          labelMultiline={false}
          identifier="CONS5"
          hidden={fieldsMetadata.cons5_email_confirmation.isHidden}
        >
          <Input
            data-testid="CONS5-input"
            id="CONS5"
            maxLength={50}
            name="CONS5"
            onBlur={handleBlur('cons5_email_confirmation')}
            onChange={handleChange('cons5_email_confirmation')}
            required
            readOnly={fieldsMetadata.cons5_email_confirmation.isReadOnly}
            value={values.cons5_email_confirmation}
          />
        </FieldWrapper>
      </FormControl>

      <FormControl
        isRequired={fieldsMetadata.cons6_phoneNumber.isRequired}
        isInError={!!errors.cons6_phoneNumber}
        errors={[errors.cons6_phoneNumber ?? '']}
      >
        <FieldWrapper
          label={t('label-phone')}
          labelMultiline={false}
          identifier="CONS6"
          hidden={fieldsMetadata.cons6_phoneNumber.isHidden}
        >
          <MaskedInput
            data-testid="CONS6-input"
            id="CONS6"
            name="CONS6"
            type="tel"
            value={values.cons6_phoneNumber}
            onAccept={(_value, maskRef) => {
              handleChange('cons6_phoneNumber')(maskRef.unmaskedValue);
            }}
            onBlur={handleBlur('cons6_phoneNumber')}
            mask={TEL_INPUT_MASK}
            readOnly={fieldsMetadata.cons6_phoneNumber.isReadOnly}
            placeholder={t('placeholder-phone')}
          />
        </FieldWrapper>
      </FormControl>

      <FormControl
        isRequired={fieldsMetadata.cons7_phoneExtension.isRequired}
        isInError={!!errors.cons7_phoneExtension}
        errors={[errors.cons7_phoneExtension ?? '']}
      >
        <FieldWrapper
          label={t('label-phone-extension')}
          labelMultiline={false}
          identifier="CONS7"
          hidden={fieldsMetadata.cons7_phoneExtension.isHidden}
        >
          <Input
            data-testid="CONS7-input"
            id="CONS7"
            name="CONS7"
            onBlur={handleBlur('cons7_phoneExtension')}
            onChange={handleChange('cons7_phoneExtension')}
            readOnly={fieldsMetadata.cons7_phoneExtension.isReadOnly}
            value={values.cons7_phoneExtension ?? ''}
          />
        </FieldWrapper>
      </FormControl>
    </div>
  );
};
